// Selection outline of a hang slot (only while a slot is selected; nothing on hover):
// - empty slot: a thin line (1.5 CSS px, the panel's ink colour) round the rectangle the frame would take,
//   just off the wall: a ring of 8 vertices, the outer ones pushed out in screen space by a constant number of
//   pixels along the wall's right / up directions as they appear on screen;
// - filled slot: the room's usual selection outline (the Interaction's inverted hull) of the frame's box.

import { BoxGeometry, BufferAttribute, BufferGeometry, Color, DoubleSide, Mesh, ShaderMaterial, Vector3, type Scene } from 'three';
import { hullGeometry, type Interaction } from '../interact/interaction';
import type { Artworks } from './artworks';
import type { SlotSet } from './slots';

/** The panel's ink, oklch(18% 0.02 262), as sRGB. */
const INK = 0x1b1f2a;
const LINE_PX = 1.5;

export class SlotOutline {
  private ring: Mesh;
  private hull: Mesh | null = null;
  private ringMat: ShaderMaterial;
  shown: string | null = null;

  constructor(private scene: Scene, private ia: Interaction, private slots: SlotSet, private artworks: Artworks) {
    this.ringMat = new ShaderMaterial({
      uniforms: {
        uColor: { value: new Color(INK) },
        uPx: { value: LINE_PX },
        uRes: ia.outlineResolution, // the same object: resizes follow
        uRight: { value: new Vector3(1, 0, 0) },
        uUp: { value: new Vector3(0, 1, 0) },
      },
      vertexShader: /* glsl */ `
        attribute vec2 aDir;
        uniform float uPx;
        uniform vec2 uRes;
        uniform vec3 uRight;
        uniform vec3 uUp;
        vec2 px( vec4 c ) { return c.xy / c.w * uRes * 0.5; }
        void main() {
          mat4 m = projectionMatrix * modelViewMatrix;
          vec4 clip = m * vec4( position, 1.0 );
          vec2 s = px( clip );
          vec2 r = px( m * vec4( position + uRight * 0.01, 1.0 ) ) - s;
          vec2 u = px( m * vec4( position + uUp * 0.01, 1.0 ) ) - s;
          vec2 off = ( length( r ) > 1e-6 ? normalize( r ) * aDir.x : vec2( 0.0 ) ) + ( length( u ) > 1e-6 ? normalize( u ) * aDir.y : vec2( 0.0 ) );
          clip.xy += off * uPx * 2.0 / uRes * clip.w;
          gl_Position = clip;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        void main() {
          gl_FragColor = vec4( uColor, 1.0 );
          #include <colorspace_fragment>
        }`,
      depthWrite: false,
      side: DoubleSide, // the winding depends on the wall
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
      toneMapped: false,
    });
    this.ring = new Mesh(new BufferGeometry(), this.ringMat);
    this.ring.name = 'art:slot_outline';
    this.ring.matrixAutoUpdate = false;
    this.ring.frustumCulled = false;
    this.ring.renderOrder = 21;
    this.ring.visible = false;
    scene.add(this.ring);
  }

  /** Remove the outline from the scene and free its geometry and material (a room switch). */
  dispose(): void {
    this.show(null);
    this.ring.removeFromParent();
    this.ring.geometry.dispose();
    this.ringMat.dispose();
  }

  /** Show the outline for slot `id` (null: none). Call again after the slot's picture changed. */
  show(id: string | null): void {
    this.shown = id;
    this.ring.visible = false;
    if (this.hull) {
      this.hull.removeFromParent();
      this.hull.geometry.dispose();
      this.hull = null;
    }
    const slot = id ? this.slots.get(id) : undefined;
    if (!slot) return;
    const pl = slot.pl;
    if (this.artworks.has(slot.id)) {
      const box = this.artworks.frameBox(slot.id)!;
      const depth = box.front - box.back;
      const g = new BoxGeometry(box.w, box.h, depth);
      const tmp = new Mesh(g);
      const centre = pl.origin.clone().addScaledVector(pl.normal, box.back + depth / 2);
      tmp.matrixWorld.makeBasis(pl.right, pl.up, pl.normal).setPosition(centre);
      this.hull = new Mesh(hullGeometry([tmp]), this.ia.outlineMaterial);
      g.dispose();
      this.hull.matrixAutoUpdate = false;
      this.hull.frustumCulled = false;
      this.hull.renderOrder = 21;
      this.hull.name = 'art:frame_outline';
      this.scene.add(this.hull);
      return;
    }
    // empty: the frame's outer rectangle, 1.5 mm in front of the wall (in front of the beads for the large frame)
    const hw = slot.layout.outerW / 2, hh = slot.layout.outerH / 2;
    const z = slot.frame.hang + 0.0015;
    const corners: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    const pos = new Float32Array(8 * 3);
    const dir = new Float32Array(8 * 2);
    const p = new Vector3();
    corners.forEach(([sx, sy], i) => {
      p.copy(pl.origin).addScaledVector(pl.right, sx * hw).addScaledVector(pl.up, sy * hh).addScaledVector(pl.normal, z);
      for (const k of [0, 1]) {
        const v = i * 2 + k;
        pos.set([p.x, p.y, p.z], v * 3);
        dir.set(k ? [sx, sy] : [0, 0], v * 2);
      }
    });
    const idx: number[] = [];
    for (let i = 0; i < 4; i++) {
      const a = i * 2, b = ((i + 1) % 4) * 2; // inner a, outer a+1, inner b, outer b+1
      idx.push(a, b, b + 1, a, b + 1, a + 1);
    }
    this.ring.geometry.dispose();
    const g = (this.ring.geometry = new BufferGeometry());
    g.setAttribute('position', new BufferAttribute(pos, 3));
    g.setAttribute('aDir', new BufferAttribute(dir, 2));
    g.setIndex(idx);
    this.ringMat.uniforms.uRight!.value.copy(pl.right);
    this.ringMat.uniforms.uUp!.value.copy(pl.up);
    this.ring.visible = true;
  }
}
